import { useEffect, useRef, useState } from 'react';
import { Avatar, Field, StatusBadge } from '../components/ui.jsx';
import { api, ApiError } from '../api/client.js';
import { useAuth } from '../state/AuthContext.jsx';
import { useMeta } from '../state/MetaContext.jsx';
import { useToast } from '../state/ToastContext.jsx';
import { useTitle } from '../lib/hooks.js';
import { promptInstall, subscribeInstallState } from '../lib/pwa.js';

const KYC_COPY = {
  not_required: 'Verification is only for landlord accounts.',
  unsubmitted: 'Upload a national ID card, passport or business registration and we will verify your account. Verified landlords get a badge on every listing.',
  pending: 'Your document is with our team. Verification normally takes a working day.',
  verified: 'Your account is verified. Tenants see the badge on your listings.',
  rejected: 'We could not verify the document you sent. Upload a clearer copy.'
};

/**
 * Adding the site to a phone's home screen or a computer's desktop.
 *
 * Every browser handles this differently and only some of them let a page ask,
 * so rather than one button that works in half of them, each case says what it
 * can actually do: Chrome and Edge get the real prompt, Safari gets pointed at
 * the Share menu, and anything else is told where its own menu item lives.
 */
const InstallPanel = () => {
  const [state, setState] = useState({ status: 'unsupported' });
  const [busy, setBusy] = useState(false);

  useEffect(() => subscribeInstallState(setState), []);

  const install = async () => {
    setBusy(true);
    try {
      await promptInstall();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel">
      <h2>Install the app</h2>

      {state.status === 'installed' ? (
        <p className="field-hint">
          This device already has it. Open it from your home screen or desktop and it runs in its own window, without
          the browser bars.
        </p>
      ) : (
        <p className="field-hint">
          Put Cameroon Homes on your phone's home screen or your computer's desktop. It opens in its own window, starts
          faster, and still opens when the network is poor.
        </p>
      )}

      {state.status === 'ready' ? (
        <button type="button" className="btn btn-primary btn-block" onClick={install} disabled={busy}>
          {busy ? 'Opening the installer...' : 'Install on this device'}
        </button>
      ) : null}

      {state.status === 'ios' ? (
        <ol className="plain-list install-steps">
          <li>
            Tap <strong>Share</strong> at the bottom of Safari.
          </li>
          <li>
            Choose <strong>Add to Home Screen</strong>.
          </li>
          <li>
            Tap <strong>Add</strong>.
          </li>
        </ol>
      ) : null}

      {state.status === 'unsupported' ? (
        <p className="field-hint">
          Your browser has not offered to install yet. In Chrome or Edge, look for the install icon at the right of the
          address bar, or open the browser menu and choose <strong>Install</strong> or{' '}
          <strong>Add to Home screen</strong>. Firefox on a computer cannot install sites.
        </p>
      ) : null}
    </section>
  );
};

/**
 * A landlord's logo, printed at the top of their receipts, lease agreements and
 * sale agreements - on the pages they print and on the PDFs that are emailed.
 */
const LogoPanel = ({ user, updateUser }) => {
  const toast = useToast();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      toast.error('Use a PNG or JPG image. Other formats cannot be printed on PDF documents.');
      return;
    }
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { logoUrl } = await api.upload('/users/me/logo', formData);
      updateUser({ logoUrl });
      toast.success('Logo saved. It now appears on your receipts and agreements.');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.del('/users/me/logo');
      updateUser({ logoUrl: null });
      toast.success('Logo removed. Your documents carry your name only.');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel">
      <h2>Logo on your documents</h2>
      <p className="muted">
        Printed at the top of your rent and utility receipts, lease agreements and sale agreements, above{' '}
        {user.businessName || user.name}.
      </p>
      {user.logoUrl ? (
        <div className="logo-preview">
          <img src={user.logoUrl} alt="Your logo" />
        </div>
      ) : (
        <p className="field-hint">No logo yet. A PNG with a transparent background looks best; JPG works too.</p>
      )}
      <div className="row-actions">
        <button type="button" className="btn btn-outline btn-sm" onClick={() => input.current?.click()} disabled={busy}>
          {busy ? 'Saving...' : user.logoUrl ? 'Change logo' : 'Add a logo'}
        </button>
        {user.logoUrl ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={remove} disabled={busy}>
            Remove
          </button>
        ) : null}
      </div>
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg"
        hidden
        onChange={(event) => {
          upload(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
    </section>
  );
};

// The same limit the server holds "About you" to.
const BIO_MAX = 3000;

const Profile = () => {
  useTitle('Profile and settings');
  const { user, updateUser, signOut } = useAuth();
  const { cities } = useMeta();
  const toast = useToast();

  const [form, setForm] = useState({
    name: user.name,
    phone: user.phone || '',
    city: user.city || '',
    bio: user.bio || '',
    businessName: user.businessName || ''
  });
  const [errors, setErrors] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);

  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '' });
  const [savingPassword, setSavingPassword] = useState(false);

  const avatarInput = useRef(null);
  const kycInput = useRef(null);
  const [uploading, setUploading] = useState(false);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  const saveProfile = async (event) => {
    event.preventDefault();
    setSavingProfile(true);
    setErrors({});
    try {
      const { user: updated } = await api.patch('/users/me', {
        name: form.name.trim(),
        phone: form.phone.trim(),
        city: form.city || null,
        bio: form.bio.trim() || null,
        ...(user.role === 'landlord' ? { businessName: form.businessName.trim() || null } : {})
      });
      updateUser(updated);
      toast.success('Profile saved.');
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else toast.error(error.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async (event) => {
    event.preventDefault();
    setSavingPassword(true);
    try {
      await api.post('/auth/change-password', passwords);
      setPasswords({ currentPassword: '', newPassword: '' });
      toast.success('Password changed.');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingPassword(false);
    }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { avatarUrl } = await api.upload('/users/me/avatar', formData);
      updateUser({ avatarUrl });
      toast.success('Photo updated.');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  const uploadKyc = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const { kycStatus } = await api.upload('/users/me/kyc', (() => {
        const formData = new FormData();
        formData.append('file', file);
        return formData;
      })());
      updateUser({ kycStatus });
      toast.success('Document sent for verification.');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="container section">
      <h1 className="page-title">Profile and settings</h1>

      <div className="profile-layout">
        <section className="panel">
          <h2>Your details</h2>

          <div className="avatar-row">
            <Avatar name={user.name} url={user.avatarUrl} size={72} />
            <div>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => avatarInput.current?.click()}
                disabled={uploading}
              >
                {user.avatarUrl ? 'Change photo' : 'Add a photo'}
              </button>
              <p className="field-hint">A face or a logo makes landlords and tenants more comfortable dealing with you.</p>
              <input
                ref={avatarInput}
                type="file"
                accept="image/*"
                hidden
                onChange={(event) => uploadAvatar(event.target.files?.[0])}
              />
            </div>
          </div>

          <form className="stack" onSubmit={saveProfile}>
            <Field label="Full name" error={errors.name} required>
              <input type="text" value={form.name} onChange={(event) => set({ name: event.target.value })} required />
            </Field>

            <div className="grid-2">
              <Field label="Phone number" error={errors.phone}>
                <input type="tel" value={form.phone} onChange={(event) => set({ phone: event.target.value })} />
              </Field>

              <Field label="Town" error={errors.city}>
                <select value={form.city} onChange={(event) => set({ city: event.target.value })}>
                  <option value="">Not set</option>
                  {cities.map((city) => (
                    <option key={city.name} value={city.name}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            {user.role === 'landlord' ? (
              <Field label="Business name" error={errors.businessName}>
                <input
                  type="text"
                  value={form.businessName}
                  onChange={(event) => set({ businessName: event.target.value })}
                />
              </Field>
            ) : null}

            <Field
              label="About you"
              hint={`${user.role === 'landlord' ? 'Tenants read this on your profile.' : 'Optional.'} ${form.bio.length.toLocaleString('en')} of ${BIO_MAX.toLocaleString('en')} characters.`}
              error={errors.bio || (form.bio.length > BIO_MAX ? `That is ${(form.bio.length - BIO_MAX).toLocaleString('en')} characters too long.` : undefined)}
            >
              <textarea rows="8" value={form.bio} onChange={(event) => set({ bio: event.target.value })} />
            </Field>

            <div className="row-actions">
              <button type="submit" className="btn btn-primary" disabled={savingProfile}>
                {savingProfile ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </form>

          <p className="field-hint">
            Email is <strong>{user.email}</strong>. Contact support if you need it changed.
          </p>
        </section>

        <div className="profile-side">
          {user.role === 'landlord' ? <LogoPanel user={user} updateUser={updateUser} /> : null}

          {user.role === 'landlord' ? (
            <section className="panel">
              <h2>Verification</h2>
              <p className="kyc-status">
                <StatusBadge status={user.kycStatus} />
              </p>
              <p>{KYC_COPY[user.kycStatus]}</p>
              {['unsubmitted', 'rejected', 'pending'].includes(user.kycStatus) ? (
                <>
                  <button
                    type="button"
                    className="btn btn-outline btn-block"
                    onClick={() => kycInput.current?.click()}
                    disabled={uploading}
                  >
                    {user.kycStatus === 'pending' ? 'Replace the document' : 'Upload a document'}
                  </button>
                  <input
                    ref={kycInput}
                    type="file"
                    accept="image/*,application/pdf"
                    hidden
                    onChange={(event) => uploadKyc(event.target.files?.[0])}
                  />
                </>
              ) : null}
            </section>
          ) : null}

          <section className="panel">
            <h2>Password</h2>
            <form className="stack" onSubmit={changePassword}>
              <Field label="Current password" required>
                <input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={passwords.currentPassword}
                  onChange={(event) => setPasswords({ ...passwords, currentPassword: event.target.value })}
                />
              </Field>
              <Field label="New password" hint="At least 8 characters." required>
                <input
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={passwords.newPassword}
                  onChange={(event) => setPasswords({ ...passwords, newPassword: event.target.value })}
                />
              </Field>
              <button type="submit" className="btn btn-outline btn-block" disabled={savingPassword}>
                {savingPassword ? 'Changing...' : 'Change password'}
              </button>
            </form>
          </section>

          <InstallPanel />

          <section className="panel">
            <h2>Session</h2>
            <p className="field-hint">
              Signing out clears your token from this device only. You are also signed out automatically after two
              minutes without any activity, so an account left open on a shared phone does not stay open.
            </p>
            <button type="button" className="btn btn-ghost btn-block" onClick={signOut}>
              Sign out
            </button>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Profile;
